"use client";

import { IconButton, InputAdornment, Switch } from "@mui/material";
import { useFormikContext } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import FullLogoPrimary from "@/assets/logos/full-logo-primary.png";
import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useShowPassword from "@/hooks/use-show-password";
import { LoginMessages } from "@/messages/login/login.messages";
import { setRecoverPasswordEmailState } from "@/states/recover-password-email";
import pxToRem from "@/themes/functions/pxToRem";
import { Language } from "@/types/language.types";

import { LoginFormData } from "../../types";

type Props = {
  messages: LoginMessages;
  isLoading: boolean;
};

function LoginForm({ messages, isLoading }: Props) {
  const { handlePasswordVisibility, inputType, inputIcon } = useShowPassword();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirectUrl = searchParams.get("redirect");
  const [rememberMe, setRememberMe] = useState(false);
  const handleSetRememberMe = () => setRememberMe(!rememberMe);
  const { lang } = useLanguage();
  const formik = useFormikContext<LoginFormData>();
  const { values, errors, submitForm, handleChange, setFieldValue } = formik;

  useEffect(() => {
    const savedLogin = localStorage.getItem("rememberedLogin");
    if (savedLogin || savedLogin === "true") {
      setRememberMe(true);
    }
  }, [setFieldValue]);

  const onRegisterRedirect = () => navigate(registerUrl);

  const onRecoverPasswordRedirect = () => {
    navigate(recoverPasswordUrl);
    setRecoverPasswordEmailState(values.login);
  };

  const recoverPasswordUrl = redirectUrl
    ? ROUTE_URLS.recoverPasswordRedirect({ lang: lang as Language, redirect: redirectUrl })
    : ROUTE_URLS.recoverPassword(lang as Language);

  const registerUrl = ROUTE_URLS.register(lang as Language);

  const handleLogin = () => {
    if (rememberMe) {
      localStorage.setItem("rememberedLogin", "true");
    } else {
      localStorage.removeItem("rememberedLogin");
    }
    submitForm();
  };

  const sharedInputStyles = {
    backgroundColor: "#fff",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "white !important",
      },
      "&:hover fieldset": {
        borderColor: "white !important",
      },
      "&.Mui-focused fieldset": {
        borderColor: "white !important",
      },
    },
  };

  return (
    <MDBox width={"70%"} maxWidth={pxToRem(400)}>
      <MDBox mb={2}>
        <img src={FullLogoPrimary} height={64} alt={messages.logoAlt} />
      </MDBox>

      <MDBox mb={1}>
        <MDTypography variant="h3" color="white" fontWeight={"bold"}>
          {messages.loginTitle}
        </MDTypography>

        <MDTypography variant="body2" color="white" mt={0.5}>
          {messages.loginDescription}{" "}
          <MDTypography
            variant="body2"
            component="span"
            fontWeight="bold"
            color="white"
            sx={{ textDecoration: "underline", marginLeft: "4px" }}
          >
            {messages.loginStepHighlight}
          </MDTypography>
        </MDTypography>
      </MDBox>

      <MDBox
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            submitForm();
          }
        }}
        pt={2}
        pb={2}
      >
        <MDBox mb={2}>
          <MDTypography variant="button" color="white">
            {messages.emailLabel}
          </MDTypography>
          <FormField
            fullWidth
            type="email"
            name="email"
            onChange={handleChange}
            value={values.email}
            error={!!errors.email}
            slotProps={{ inputLabel: { shrink: true } }}
            required
            sx={sharedInputStyles}
          />
        </MDBox>
        <MDBox mb={2}>
          <MDTypography variant="button" color="white">
            {messages.passwordLabel}
          </MDTypography>
          <FormField
            fullWidth
            type={inputType}
            value={values.password}
            error={!!errors.password}
            onChange={handleChange}
            disableIcon
            name="password"
            required
            sx={sharedInputStyles}
            InputProps={{
              endAdornment: errors.password ? null : (
                <InputAdornment position="end">
                  <IconButton
                    sx={{ color: "white !important" }}
                    onClick={handlePasswordVisibility}
                  >
                    {inputIcon}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            slotProps={{
              inputLabel: { shrink: true },
            }}
          />
        </MDBox>
        <MDBox display="flex" alignItems="center" ml={-1}>
          <Switch checked={rememberMe} onChange={handleSetRememberMe} />
          <MDTypography
            variant="button"
            fontWeight="regular"
            color="white"
            onClick={handleSetRememberMe}
            sx={{ cursor: "pointer", userSelect: "none", ml: 0 }}
          >
            &nbsp;&nbsp;{messages.rememberMe}
          </MDTypography>
        </MDBox>

        <MDBox mt={2} mb={1}>
          <MDButton
            variant="contained"
            color="white"
            circular
            fullWidth
            size="medium"
            onClick={handleLogin}
            isLoading={isLoading}
          >
            <MDTypography
              variant="button"
              textTransform={"uppercase"}
              fontWeight="bold"
              color="primary"
            >
              {messages.loginAction}
            </MDTypography>
          </MDButton>
        </MDBox>
        <MDBox mt={2} mb={1}>
          <MDTypography
            variant="button"
            fontWeight="regular"
            color="dark"
            onClick={onRecoverPasswordRedirect}
            sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
          >
            &nbsp;&nbsp;{messages.forgotPassword}
          </MDTypography>
        </MDBox>

        <MDBox mt={3} mb={1} textAlign="center">
          <MDTypography variant="button" color="dark">
            {messages.dontHaveAnAccount} &nbsp;
            <MDTypography
              variant="button"
              color="dark"
              fontWeight="medium"
              textGradient
              sx={{ cursor: "pointer" }}
              onClick={onRegisterRedirect}
            >
              {messages.clickText}
            </MDTypography>
          </MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

export default LoginForm;
